import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Building2, Globe2, Package, MessageSquare, Filter } from "lucide-react";

const TYPE_TABS = [
  { key: "all", label: "All" },
  { key: "b2b", label: "Contract Manufacturing" },
  { key: "general", label: "General" },
  { key: "wholesale", label: "Wholesale" },
];

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/inquiries")
      .then((r) => { if (!cancelled) setInquiries(r.data || []); })
      .catch((err) => { if (!cancelled) toast.error(formatApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = tab === "all" ? inquiries : inquiries.filter((i) => i.inquiry_type === tab);

  const counts = TYPE_TABS.reduce((acc, t) => {
    acc[t.key] = t.key === "all" ? inquiries.length : inquiries.filter((i) => i.inquiry_type === t.key).length;
    return acc;
  }, {});

  return (
    <div data-testid="admin-inquiries-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-24 pb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Admin • Inquiries</p>
            <h1 className="font-heading text-5xl md:text-6xl mt-3 tracking-tighter">Lead inbox.</h1>
            <p className="mt-4 text-faytex-muted max-w-2xl">
              Every message submitted from the Contract Manufacturing and contact forms lands here — newest first.
            </p>
          </div>
          <Link
            to="/admin"
            data-testid="admin-inq-back"
            className="rounded-full bg-gray-100 hover:bg-gray-200 text-faytex-ink px-5 py-2.5 text-sm font-semibold"
          >
            ← Catalog admin
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-faytex-muted font-semibold">
            <Filter className="w-3.5 h-3.5" /> Filter
          </span>
          <div className="flex gap-2 flex-wrap" data-testid="admin-inq-tabs">
            {TYPE_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-inq-tab-${t.key}`}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  tab === t.key
                    ? "bg-faytex-pink text-white"
                    : "bg-faytex-pinkLight/60 text-faytex-pinkHover hover:bg-faytex-pink hover:text-white"
                }`}
              >
                {t.label} <span className="opacity-70">({counts[t.key] || 0})</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-faytex-muted py-16 text-center">Loading inquiries…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-inq-empty" className="rounded-3xl border border-dashed border-gray-200 p-16 text-center">
            <Package className="w-8 h-8 mx-auto text-faytex-muted" />
            <p className="mt-4 font-heading text-2xl">No inquiries yet in this filter.</p>
            <p className="text-sm text-faytex-muted mt-2">New submissions appear here in real time.</p>
          </div>
        ) : (
          <div data-testid="admin-inq-list" className="space-y-3">
            {filtered.map((q) => {
              const open = expanded === q.id;
              return (
                <article
                  key={q.id}
                  data-testid={`inq-row-${q.id}`}
                  className="bg-white rounded-2xl border border-gray-100 shadow-soft hover:shadow-hover transition-shadow"
                >
                  <button
                    onClick={() => setExpanded(open ? null : q.id)}
                    className="w-full text-left px-5 md:px-6 py-4 flex flex-wrap items-center gap-4"
                    data-testid={`inq-toggle-${q.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading text-xl truncate">{q.name}</p>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          q.inquiry_type === "b2b" ? "bg-faytex-pink text-white" :
                          q.inquiry_type === "wholesale" ? "bg-faytex-blue text-white" :
                          "bg-gray-100 text-faytex-ink"
                        }`}>{q.inquiry_type}</span>
                      </div>
                      <p className="text-xs text-faytex-muted mt-1 truncate">
                        {q.email}{q.company ? ` • ${q.company}` : ""}{q.country ? ` • ${q.country}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-faytex-muted">{fmt(q.created_at)}</p>
                      {q.monthly_volume && (
                        <p className="text-[11px] mt-1 inline-block bg-faytex-pinkLight text-faytex-pinkHover px-2 py-0.5 rounded-full">
                          {q.monthly_volume}
                        </p>
                      )}
                    </div>
                  </button>
                  {open && (
                    <div
                      data-testid={`inq-detail-${q.id}`}
                      className="border-t border-gray-100 px-5 md:px-6 py-5 grid md:grid-cols-2 gap-4 text-sm"
                    >
                      <ContactLine icon={<Mail className="w-4 h-4" />} label="Email" value={q.email} href={`mailto:${q.email}`} />
                      {q.phone && <ContactLine icon={<Phone className="w-4 h-4" />} label="Phone" value={q.phone} href={`tel:${q.phone}`} />}
                      {q.company && <ContactLine icon={<Building2 className="w-4 h-4" />} label="Company" value={q.company} />}
                      {q.country && <ContactLine icon={<Globe2 className="w-4 h-4" />} label="Country" value={q.country} />}
                      <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-widest text-faytex-muted font-semibold flex items-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5" /> Message
                        </p>
                        <p className="mt-2 whitespace-pre-line leading-relaxed">{q.message}</p>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function ContactLine({ icon, label, value, href }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-faytex-pink mt-0.5">{icon}</span>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-faytex-muted font-semibold">{label}</p>
        {href ? (
          <a href={href} className="text-faytex-ink hover:text-faytex-pink underline underline-offset-2">{value}</a>
        ) : (
          <p className="text-faytex-ink">{value}</p>
        )}
      </div>
    </div>
  );
}
